<template>
  <b-row class="group-tree-select-wrapper">
    <b-col cols="12">
      <div class="group-tree-select-row">
        <treeselect
          ref="treeselect"
          name="group-tree-select"
          :placeholder="
            loading && groupsTree.length === 0
              ? labels.loading
              : user.companyName
          "
          :clearAllText="labels.cleanFilter"
          :noOptionsText="labels.wait"
          :noResultsText="labels.searchNotFound"
          showCountOf="ALL_DESCENDANTS"
          :flat="true"
          :autoSelectDescendants="autoSelectDescendants"
          :autoDeselectDescendants="autoDeselectDescendants"
          :sort-value-by="`INDEX`"
          :multiple="multiple"
          :options="groupsTree"
          :clearable="clearable"
          :searchable="searchable"
          :open-on-click="openOnClick"
          :open-on-focus="openOnFocus"
          :close-on-select="closeOnSelect"
          :show-count="showCount"
          :always-open="alwaysOpen"
          :limit="limit"
          :limitText="count => labels.counter.replace('$count', count)"
          :value="selectedGroups"
          @input="updateSelectedGroups"
        >
          <label
            slot="option-label"
            slot-scope="{
              node,
              shouldShowCount,
              count,
              labelClassName,
              countClassName,
            }"
            :class="labelClassName"
          >
            <span
              :title="node.label"
              :class="{ 'inactive-group': node.raw.inactive }"
            >
              {{ node.label }}
            </span>
            <span v-if="shouldShowCount" :class="countClassName">
              ({{ count }})
            </span>
          </label>
          <div slot="value-label" slot-scope="{ node }">
            <span :class="{ 'inactive-group': node.raw.inactive }">
              {{ node.label }}
            </span>
          </div>
        </treeselect>

        <b-dropdown
          ref="filter-dropdown"
          @click="applyFilter()"
          class="filter-dropdown"
          variant="link"
          right
          split
          split-variant="outline-primary"
          :text="labels.applyFilter"
        >
          <b-dropdown-header>{{ labels.subgroups }}</b-dropdown-header>
          <b-dropdown-form>
            <b-form-checkbox v-model="autoSelectDescendants">
              <small class="clickable">{{ labels.autoSelect }}</small>
            </b-form-checkbox>
            <b-form-checkbox
              class="clickable"
              v-model="autoDeselectDescendants"
            >
              <small class="clickable">{{ labels.autoUnselect }}</small>
            </b-form-checkbox>
            <b-form-checkbox class="clickable" v-model="showAll">
              <small class="clickable">{{ labels.showAll }}</small>
            </b-form-checkbox>
          </b-dropdown-form>

          <b-dropdown-divider></b-dropdown-divider>

          <b-dropdown-header>
            {{ labels.favouritesFilter }}
            <b-button
              :disabled="selectedGroups.length === 0 || userFilterAddLoading"
              variant="outline-primary"
              class="add-btn"
              @click="saveUserFilter()"
            >
              <i
                class="mdi mdi-loading mdi-spin mr-1"
                v-if="userFilterAddLoading"
              >
              </i>
              <i v-else class="mdi mdi-content-save mr-1"></i>
              <small>{{ labels.save }}</small>
            </b-button>
          </b-dropdown-header>

          <b-dropdown-text
            v-for="(filter, index) in userFilters"
            :key="`user-filter-${index}`"
            :id="`user-filter-${index}`"
            @click="applyFavouriteFilter(filter.value.selectedGroupsOids)"
          >
            <small>{{ `${labels.filter} ${index + 1}` }}</small>
            <b-button
              :title="labels.remove"
              :disabled="userFilterAddLoading"
              variant="outline-link"
              @click.stop="removeUserFilter(filter.id)"
              size="lg"
            >
              <i class="material-icons">delete_forever</i>
            </b-button>
            <b-tooltip
              variant="primary"
              :target="`user-filter-${index}`"
              placement="left"
              boundary="viewport"
            >
              <div class="bold">{{ `${labels.filter} ${index + 1}` }}</div>
              <div
                v-for="(group, idx) in filter.value.selectedGroups"
                :key="idx"
              >
                <small>{{ group }}</small>
              </div>
            </b-tooltip>
          </b-dropdown-text>
        </b-dropdown>
      </div>
    </b-col>
  </b-row>
</template>

<script>
import { mapGetters } from 'vuex'
import Treeselect from '@riophae/vue-treeselect'
import '@riophae/vue-treeselect/dist/vue-treeselect.css'
import { getUser } from '@/service/user'

export default {
  components: { Treeselect },
  data() {
    return {
      loading: false,
      value: null,
      multiple: true,
      clearable: true,
      searchable: true,
      openOnClick: true,
      openOnFocus: true,
      closeOnSelect: true,
      showCount: true,
      alwaysOpen: false,
      autoSelectDescendants: true,
      autoDeselectDescendants: false,
      showAll: false,
    }
  },
  computed: {
    ...mapGetters({
      resources: 'resources',
      groupsTree: 'user/groupsTree',
      selectedGroups: 'comparation/selectedGroups',
      userFilters: 'comparation/userFilters',
      userFilterAddLoading: 'comparation/userFilterAddLoading',
    }),

    user() {
      return getUser()
    },

    labels() {
      return {
        ...this.resources.component.groupsTreeSelect,
        ...this.resources.component.groupsTreeMultiSelectFilter,
      }
    },

    limit() {
      return this.showAll ? Infinity : 1
    },
  },
  created() {
    this.loadGroupsTree()
    this.$store.dispatch('comparation/loadUserFilters')
  },
  methods: {
    updateSelectedGroups(value) {
      this.$refs['filter-dropdown'].hide()
      this.$store.dispatch('comparation/updateSelectedGroups', value)
    },
    async loadGroupsTree() {
      this.loading = true
      await this.$store.dispatch('user/loadGroupsTree')
      this.loading = false
    },
    applyFilter() {
      this.$emit('loadReportsGroups', this.selectedGroups)
    },
    applyFavouriteFilter(groups) {
      this.updateSelectedGroups(groups)
      this.applyFilter()
      this.$refs['filter-dropdown'].hide()
    },
    async saveUserFilter() {
      const newFilter = {
        id: `groups-filter-${new Date().getTime()}`,
        value: {
          selectedGroupsOids: this.selectedGroups,
          selectedGroups: this.selectedGroups.map(group => {
            return this.$refs['treeselect'].getNode(group).label
          }),
        },
      }
      const filters = [...this.userFilters, newFilter]
      await this.$store.dispatch('comparation/saveUserFilter', filters)
      this.$refs['filter-dropdown'].hide()
    },
    removeUserFilter(id) {
      const filters = this.userFilters.filter(f => f.id !== id)
      this.$store.dispatch('comparation/saveUserFilter', filters)
    },
  },
}
</script>

<style scoped lang="scss">
@import '~@/styles/custom/colors';

.group-tree-select-wrapper {
  padding-top: 10px;
  flex-grow: 1;

  .group-tree-select-row {
    display: flex;

    .filter-dropdown {
      min-width: 180px;
      background-color: map-get($theme-colors, 'white') !important;
      box-shadow: 0px 2px 0px 1px map-get($theme-colors, 'gray08');
      border: 1px solid map-get($theme-colors, 'gray07');
      border-left: 0;
      border-top-right-radius: 5px;
      border-bottom-right-radius: 5px;

      /deep/ .btn {
        border-radius: 0px !important;
        height: 100% !important;
        box-shadow: none !important;
      }

      /deep/ .custom-control-label::before {
        box-shadow: none !important;
      }

      /deep/ .btn-outline-primary:first-of-type {
        border: 0;
        border-right: 1px solid map-get($theme-colors, 'gray07');
      }

      /deep/ .dropdown-menu {
        min-width: 275px;
        max-width: 350px;
        max-height: 350px;
        overflow: auto;

        .dropdown-header {
          display: flex;
          justify-content: space-between;
          align-items: center;

          .add-btn {
            border: 0 !important;
            border-radius: 5px !important;
          }
        }

        .b-dropdown-text {
          display: flex;
          justify-content: space-between;
          align-items: center;
          cursor: pointer;

          &:hover {
            background-color: map-get($theme-colors, 'gray08');
          }
        }
      }
    }

    .clearable-icon {
      font-size: 1.8rem;
    }

    /deep/ .vue-treeselect {
      font-size: 1.1rem;

      .vue-treeselect__menu {
        overflow-x: auto;
      }

      .vue-treeselect__checkbox--checked {
        border-color: map-get($theme-colors, 'primary') !important;
        background: map-get($theme-colors, 'primary') !important;
      }

      .vue-treeselect__multi-value-item {
        background-color: map-get($theme-colors, 'gray08');
        color: map-get($theme-colors, 'primary') !important;

        .vue-treeselect__value-remove {
          color: map-get($theme-colors, 'primary') !important;
        }

        .vue-treeselect__multi-value-label {
          white-space: normal;
        }

        .inactive-group {
          color: map-get($theme-colors, 'gray02');
        }
      }

      .vue-treeselect__placeholder {
        margin-top: 7px;
        color: map-get($theme-colors, 'primary') !important;
      }

      .vue-treeselect__control {
        height: 50px !important;
        box-shadow: 0px 2px 0px 1px map-get($theme-colors, 'gray08');
        border: 1px solid map-get($theme-colors, 'gray07');
        border-top-right-radius: 0px;
        border-bottom-right-radius: 0px;

        .vue-treeselect__input {
          background-color: map-get($theme-colors, 'white') !important;
        }
      }

      .vue-treeselect__label-container {
        &:hover {
          .vue-treeselect__checkbox--unchecked {
            border-color: map-get($theme-colors, 'primary');
          }
        }

        .vue-treeselect__label {
          overflow: inherit;

          .inactive-group {
            color: map-get($theme-colors, 'gray01');
          }
        }
      }

      .vue-treeselect__option--selected {
        background-color: map-get($theme-colors, 'gray08');

        .inactive-group {
          font-weight: normal;
          color: map-get($theme-colors, 'gray02');
        }
      }

      .vue-treeselect__single-value {
        margin-top: 7px;

        .inactive-group {
          color: map-get($theme-colors, 'gray01');
        }
      }
    }
  }
}
</style>
